import Column from '@app/components/layout/Column'
import { Title } from '@app/components/core/typography/Title'
import { Link, useLocation } from 'wouter'
import { useMemo, useState } from 'react'
import { TextInput } from '@app/components/core/input/text/TextInput'
import { faSearch } from '@fortawesome/pro-solid-svg-icons'
import { Text } from '@app/components/core/typography/Text'
import { useTranslations } from '@i18n/useLanguage'

const KnowledgeHubModuleSidebarSection = ({
  title,
  knowledgeHubItems,
  activeSlug,
}: {
  title: string
  knowledgeHubItems?: { slug: string; title: string }[]
  activeSlug?: string
}) => {
  const [location] = useLocation()

  return (
    <Column gap={0} style={{ marginTop: '0.5rem', width: '100%' }}>
      <Title.H4>{title}</Title.H4>

      {knowledgeHubItems?.map((ki) => {
        const isActive = activeSlug === ki.slug

        return (
          <Link
            key={ki.slug}
            href={`${location}?slug=${ki.slug}`}
            style={{
              textDecoration: 'none',
              width: '100%',
              padding: '0.75rem 0.5rem',
              backgroundColor: isActive
                ? 'var(--palette-steady-stone)'
                : 'transparent',
              borderRadius: '0.5rem',
              color: 'var(--brand-secondary)',
            }}
          >
            <Text.Medium
              style={{
                '&:hover': { color: 'var(--palette-blue-500)' },
              }}
            >
              {ki.title}
            </Text.Medium>
          </Link>
        )
      })}
    </Column>
  )
}

interface KnowledgeHubModuleSidebarProps {
  sections?: {
    id: string
    slug: string
    title: string
    knowledgeHubItems?: { slug: string; title: string }[]
  }[]
  activeSlug?: string
}

export const KnowledgeHubModuleSidebar = ({
  sections,
  activeSlug,
}: KnowledgeHubModuleSidebarProps) => {
  const t = useTranslations().app.knowledgeHubPage
  const [searchField, setSearchField] = useState('')

  const filteredSections = useMemo(() => {
    if (!searchField) {
      return sections
    }

    return sections
      ?.map((section) => {
        const filteredItems = section.knowledgeHubItems?.filter((item) =>
          item.title.toLowerCase().includes(searchField.toLowerCase()),
        )
        return {
          ...section,
          knowledgeHubItems: filteredItems,
        }
      })
      .filter(
        (section) =>
          section.knowledgeHubItems && section.knowledgeHubItems.length > 0,
      )
  }, [sections, searchField])

  return (
    <>
      <Column gap="1rem">
        <TextInput
          text={searchField}
          setText={setSearchField}
          placeholder={t.searchPlaceholder}
          icon={faSearch}
          style={{ width: '100%', marginBottom: '0.5rem' }}
        />
        {filteredSections?.map(({ id, title, knowledgeHubItems }) => (
          <KnowledgeHubModuleSidebarSection
            key={id}
            title={title}
            knowledgeHubItems={knowledgeHubItems}
            activeSlug={activeSlug}
          />
        ))}
      </Column>
    </>
  )
}
